import React, { useEffect, useMemo, useState } from 'react';
//@ts-ignore
import Modal from 'react-awesome-modal';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import { SvgElement, icontypesEnum } from '../assets/svgElement';
import Select from 'react-select';
import { Page, Text, View, Document, StyleSheet, Image, pdf } from '@react-pdf/renderer';

type RowStatus = 'idle' | 'uploading' | 'done' | 'error';

type Row = {
  id: number;
  number: string;
  manual: boolean;        // true = number typed by hand (not from the awaiting list)
  file: File | null;
  previewUrl: string | null;
  status: RowStatus;
  message?: string;
  // Shipment context. Awaiting rows carry it from the list; manual rows start
  // empty and are filled in by a lookup once a number is entered.
  destination: string;    // ReceiverAddState
  address: string;        // Add1 + Add2 + City, cleaned
  hoursAwaiting: number | null;
  deliveredAt: string;    // POD event_datetime — when the shipment was delivered
  receiverName: string;
  receiverPhone: string;
  weight: string;
  pcs: string;
  manifestName: string;
  runManifestName: string;
  fleetName: string;
  rider: string;
  hydrated?: boolean;     // manual row has been looked up (found or not)
  notFound?: boolean;     // looked up and no such shipment exists
};

type FilterOption = { id: string; name: string; count: number };
type FilterOptions = {
  manifests: FilterOption[];
  run_manifests: FilterOption[];
  fleets: FilterOption[];
  riders: FilterOption[];
};

/** The four ways a batch can be grouped. Only one is active at a time — a
 *  shipment belongs to one manifest, one fleet, one rider.
 *
 *  Manifests are keyed by their slug id; fleet and rider are keyed by name,
 *  because those are resolved from the run manifest rather than an id column. */
type GroupKind = '' | 'manifest_id' | 'run_manifest_id' | 'fleet_name' | 'rider';

const GROUP_LABELS: Record<Exclude<GroupKind, ''>, string> = {
  manifest_id: 'Manifest',
  run_manifest_id: 'Run Manifest',
  fleet_name: 'Fleet',
  rider: 'Rider',
};

/** Shipment address fields are frequently filled with placeholder literals from
 *  the create-shipment defaults. Left alone they print onto a document a
 *  recipient signs, so they're dropped rather than shown. */
const PLACEHOLDERS = new Set(['empty', 'null', 'n/a', 'na', '-', '--', 'nil', 'none', 'undefined']);

const isMeaningful = (v: any) => {
  const s = String(v ?? '').trim();
  return s !== '' && !PLACEHOLDERS.has(s.toLowerCase());
};

/** Join the address parts that actually say something, de-duplicating repeats
 *  (city is often echoed inside Add1). */
const buildAddress = (src: any): string => {
  const parts = [src?.ReceiverAdd1, src?.ReceiverAdd2, src?.ReceiverAddCity]
    .filter(isMeaningful)
    .map((p: any) => String(p).trim());

  const seen = new Set<string>();
  return parts
    .filter((p) => {
      const k = p.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .join(', ');
};

/** Map an API shipment record onto the row's context fields. Shared by the
 *  awaiting list and the manual-entry lookup so both produce identical rows. */
const contextFromSource = (src: any) => ({
  destination: isMeaningful(src?.ReceiverAddState) ? String(src.ReceiverAddState).trim() : '',
  address: buildAddress(src),
  hoursAwaiting: typeof src?.hours_awaiting === 'number' ? src.hours_awaiting : null,
  // event_datetime is the POD event — i.e. when it was actually delivered. It is
  // the same field the server measures `hours_awaiting` from, so the date shown
  // and the waiting time can never disagree.
  deliveredAt: isMeaningful(src?.event_datetime) ? String(src.event_datetime).trim() : '',
  receiverName: isMeaningful(src?.ReceiverName) ? String(src.ReceiverName).trim() : '',
  receiverPhone: isMeaningful(src?.ReceiverPhone) ? String(src.ReceiverPhone).trim() : '',
  weight: src?.Weight ?? '',
  pcs: src?.NoOfItems ?? '',
  manifestName: src?.manifest_name ?? '',
  runManifestName: src?.run_manifest_name ?? '',
  fleetName: src?.fleet_name ?? '',
  rider: src?.rider ?? '',
});

/** Delivery date for display under the shipment number. Falls back to the raw
 *  string if it will not parse, so an odd value from the API is still visible
 *  rather than silently rendering nothing. */
const fmtDelivered = (value: string): string => {
  if (!value) return '';
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (isNaN(d.getTime())) return value;

  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

let rid = 1;
const makeRow = (number = '', manual = false, src: any = null): Row => ({
  id: rid++,
  number,
  manual,
  file: null,
  previewUrl: null,
  status: 'idle',
  ...contextFromSource(src),
});

/* ─────────────────────────── POD sheet PDF ───────────────────────────
 * A print-and-sign variant of the manifest document. Each shipment gets its
 * own row with a signature/date box, and the shipment number is set large and
 * well separated so a phone photo of a single signed row stays legible — that
 * photo is what gets uploaded back as the delivery proof.
 *
 * Deliberately self-contained (no shared mutable totals) so it can be rendered
 * for any arbitrary selection without side effects.
 */
const podStyles = StyleSheet.create({
  page: { padding: 24, fontSize: 10 },
  logo: { width: 90, height: 'auto', marginBottom: 12 },
  title: { fontSize: 14, textAlign: 'center', textTransform: 'uppercase', marginBottom: 4 },
  subtitle: { fontSize: 9, textAlign: 'center', color: '#555', marginBottom: 14 },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  metaText: { fontSize: 9 },
  headerRow: {
    flexDirection: 'row',
    borderBottom: '1.5px solid #000',
    borderTop: '1.5px solid #000',
    backgroundColor: '#F2F2F2',
    paddingVertical: 5,
    marginTop: 10,
  },
  row: {
    flexDirection: 'row',
    borderBottom: '1px solid #999',
    minHeight: 52,
    // Top-aligned: the destination cell now wraps to several lines, and centring
    // would drift the shipment number away from its row's baseline.
    alignItems: 'flex-start',
    paddingVertical: 6,
  },
  cellNo: { width: 20, paddingHorizontal: 2, fontSize: 9 },
  cellNumber: { width: 98, paddingHorizontal: 4 },
  cellReceiver: { width: 88, paddingHorizontal: 4, fontSize: 8 },
  cellDest: { flex: 1, paddingHorizontal: 4, fontSize: 8 },
  cellWgt: { width: 44, paddingHorizontal: 3, fontSize: 8 },
  cellSign: { width: 128, paddingHorizontal: 4 },
  headerText: { fontSize: 8, fontWeight: 'bold' },
  shipmentNumber: { fontSize: 12, fontWeight: 'bold', letterSpacing: 0.5 },
  destState: { fontSize: 9, fontWeight: 'bold' },
  destAddress: { fontSize: 7, color: '#444', marginTop: 1 },
  signBox: { borderBottom: '1px solid #000', height: 20, marginBottom: 3 },
  signLabel: { fontSize: 6, color: '#777' },
  footer: { marginTop: 18, flexDirection: 'row', justifyContent: 'space-between' },
  footerText: { fontSize: 9 },
  pageNum: { position: 'absolute', bottom: 14, right: 24, fontSize: 8, color: '#777' },
});

type PodDoc = {
  rows: Row[];
  groupLabel: string;
  generatedAt: string;
};

const PodSheet = ({ rows, groupLabel, generatedAt }: PodDoc) => {
  const totalWeight = rows.reduce((sum, r) => {
    const w = parseFloat(r.weight);
    return sum + (isNaN(w) ? 0 : w);
  }, 0);

  return (
    <Document>
      <Page style={podStyles.page} wrap>
        <View>
          <Image style={podStyles.logo} src="/images/Auth/logo.png" />
        </View>

        <Text style={podStyles.title}>Proof of Delivery Sheet</Text>
        <Text style={podStyles.subtitle}>
          Sign against each shipment below, then photograph the signed row to upload as delivery proof.
        </Text>

        <View style={podStyles.metaRow}>
          <Text style={podStyles.metaText}>Generated: {generatedAt}</Text>
          <Text style={podStyles.metaText}>Shipments: {rows.length}</Text>
        </View>
        <View style={podStyles.metaRow}>
          <Text style={podStyles.metaText}>Batch: {groupLabel || 'Manual selection'}</Text>
          <Text style={podStyles.metaText}>Total weight: {totalWeight.toFixed(3)} KG</Text>
        </View>

        {/* fixed → repeats at the top of every page for long batches */}
        <View style={podStyles.headerRow} fixed>
          <Text style={[podStyles.cellNo, podStyles.headerText]}>#</Text>
          <Text style={[podStyles.cellNumber, podStyles.headerText]}>Shipment No.</Text>
          <Text style={[podStyles.cellReceiver, podStyles.headerText]}>Receiver</Text>
          <Text style={[podStyles.cellDest, podStyles.headerText]}>Destination &amp; Address</Text>
          <Text style={[podStyles.cellWgt, podStyles.headerText]}>Wgt / Pcs</Text>
          <Text style={[podStyles.cellSign, podStyles.headerText]}>Signature &amp; Date</Text>
        </View>

        {rows.map((r, i) => (
          <View key={r.id} style={podStyles.row} wrap={false}>
            <Text style={podStyles.cellNo}>{i + 1}</Text>
            <View style={podStyles.cellNumber}>
              <Text style={podStyles.shipmentNumber}>{r.number}</Text>
            </View>
            <View style={podStyles.cellReceiver}>
              <Text>{r.receiverName || '—'}</Text>
              {!!r.receiverPhone && <Text style={{ color: '#666' }}>{r.receiverPhone}</Text>}
            </View>
            <View style={podStyles.cellDest}>
              <Text style={podStyles.destState}>{r.destination || '—'}</Text>
              {!!r.address && <Text style={podStyles.destAddress}>{r.address}</Text>}
            </View>
            <Text style={podStyles.cellWgt}>
              {r.weight || '—'} KG{'\n'}{r.pcs || 0} pcs
            </Text>
            <View style={podStyles.cellSign}>
              <View style={podStyles.signBox} />
              <Text style={podStyles.signLabel}>Name / Signature / Date</Text>
            </View>
          </View>
        ))}

        <View style={podStyles.footer}>
          <Text style={podStyles.footerText}>Delivered by: ____________________</Text>
          <Text style={podStyles.footerText}>Date: ____________________</Text>
        </View>

        <Text
          style={podStyles.pageNum}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

/**
 * Bulk Delivery Photos — attach a delivery photo to already-delivered Shein
 * shipments (which were POD'd in bulk without one).
 *
 * Two ways to get proof onto a shipment:
 *  1. Attach a photo per shipment inline — uploads immediately, one at a time.
 *  2. Print a POD sheet for a selected batch, have it signed, photograph it,
 *     then apply that single photo to every shipment on the sheet at once.
 *
 * The list can be grouped by manifest, run manifest, fleet or rider so a batch
 * maps to one physical sheet going to one person.
 */
export default function BulkDeliveryPhotos() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [awaitingTotal, setAwaitingTotal] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [groupKind, setGroupKind] = useState<GroupKind>('');
  const [groupValue, setGroupValue] = useState('');

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [buildingPdf, setBuildingPdf] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);

  const patchRow = (id: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const loadAwaiting = (kind: GroupKind = groupKind, value: string = groupValue) => {
    setLoading(true);
    setSelected(new Set());
    const params: any = { per_page: 1000 };
    if (kind && value) params[kind] = value;

    SuperAdminApis.getAwaitingDeliveryPhotos(params)
      .then((res: any) => {
        const paginator = res?.data?.data;
        setAwaitingTotal(paginator?.total ?? null);
        const list: any[] = (paginator?.data ?? []).filter((s: any) => s?.ShipmentNumber);
        setRows(list.map((s) => makeRow(s.ShipmentNumber, false, s)));
      })
      .catch(() => { setAwaitingTotal(null); setRows([]); })
      .finally(() => setLoading(false));
  };

  const loadFilterOptions = () => {
    SuperAdminApis.getAwaitingDeliveryPhotoFilters()
      .then((res: any) => setFilterOptions(res?.data?.data ?? null))
      .catch(() => setFilterOptions(null));
  };

  const openModal = () => {
    rid = 1;
    setSearch('');
    setRows([]);
    setSelected(new Set());
    setGroupKind('');
    setGroupValue('');
    setOpen(true);
    loadAwaiting('', '');
    loadFilterOptions();
  };

  const anyUploading = useMemo(
    () => rows.some((r) => r.status === 'uploading') || bulkApplying,
    [rows, bulkApplying]
  );

  const closeModal = () => {
    if (anyUploading) {
      toast.info('Please wait — a photo is still uploading.');
      return;
    }
    rows.forEach((r) => r.previewUrl && URL.revokeObjectURL(r.previewUrl));
    setOpen(false);
  };

  // Release object URLs if the component goes away mid-session. Read through a
  // ref — an empty-dep cleanup would otherwise close over the first render's
  // (empty) rows and free nothing.
  const rowsRef = React.useRef<Row[]>(rows);
  rowsRef.current = rows;
  useEffect(() => () => {
    rowsRef.current.forEach((r) => r.previewUrl && URL.revokeObjectURL(r.previewUrl));
  }, []);

  /** Fire the single upload for one row. `file`/`number` are passed explicitly to
   *  avoid racing React state right after an attach. */
  const uploadRow = async (row: Row, file: File) => {
    const num = row.number.trim();
    if (!num) {
      patchRow(row.id, { status: 'error', message: 'Enter a number first' });
      return;
    }
    patchRow(row.id, { status: 'uploading', message: undefined });
    const fd = new FormData();
    fd.append('shipment_number', num);
    fd.append('image', file);
    try {
      const res: any = await SuperAdminApis.uploadDeliveryPhoto(fd);
      if (res?.data?.status) {
        patchRow(row.id, { status: 'done' });
        if (!row.manual) setAwaitingTotal((t) => (t === null ? t : Math.max(0, t - 1)));
      } else {
        patchRow(row.id, { status: 'error', message: res?.data?.message || 'Failed' });
        toast.error(`${num}: ${res?.data?.message || 'upload failed'}`);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Upload failed';
      patchRow(row.id, { status: 'error', message: msg });
      toast.error(`${num}: ${msg}`);
    }
  };

  /** Attach a file to a row, refresh its preview, then upload right away if the
   *  shipment number is known (all awaiting rows are; manual rows may not be yet). */
  const onPickFile = (row: Row, file: File | null) => {
    if (row.previewUrl) URL.revokeObjectURL(row.previewUrl);
    const previewUrl = file ? URL.createObjectURL(file) : null;
    patchRow(row.id, { file, previewUrl, status: 'idle', message: undefined });
    if (file && row.number.trim()) uploadRow({ ...row, file, previewUrl }, file);
  };

  /** Manual rows where the number was typed after the photo — send on demand. */
  const sendManual = (row: Row) => {
    if (row.file && row.number.trim()) uploadRow(row, row.file);
  };

  /**
   * Fill in shipment context for hand-entered rows. They start life with only a
   * number, so without this the POD sheet prints a row with no receiver,
   * destination, weight or pieces.
   *
   * Returns the resolved rows so the PDF builder can use them immediately rather
   * than waiting for a state round-trip.
   */
  const hydrateManualRows = React.useCallback(async (candidates: Row[]): Promise<Row[]> => {
    const pending = candidates.filter((r) => r.manual && r.number.trim() && !r.hydrated);
    if (pending.length === 0) return candidates;

    const numbers = Array.from(new Set(pending.map((r) => r.number.trim())));

    let found: Record<string, any> = {};
    try {
      const res: any = await SuperAdminApis.lookupShipmentsForPod(numbers);
      found = res?.data?.data ?? {};
    } catch {
      // Lookup failed — leave rows unhydrated. The sheet still prints the
      // number, which is the part the operator actually signs against.
      return candidates;
    }

    const resolve = (r: Row): Row => {
      if (!r.manual || !r.number.trim() || r.hydrated) return r;
      const src = found[r.number.trim()];
      return src
        ? { ...r, ...contextFromSource(src), hydrated: true, notFound: false }
        : { ...r, hydrated: true, notFound: true };
    };

    setRows((rs) => rs.map(resolve));
    return candidates.map(resolve);
  }, []);

  // Look up hand-entered numbers shortly after typing stops, so the operator
  // sees the destination fill in and can confirm they typed the right number.
  const manualKey = rows.filter((r) => r.manual && !r.hydrated).map((r) => r.number.trim()).join('|');
  useEffect(() => {
    if (!manualKey) return;
    const t = setTimeout(() => { hydrateManualRows(rowsRef.current); }, 700);
    return () => clearTimeout(t);
  }, [manualKey, hydrateManualRows]);

  const retryFailed = () => {
    rows.filter((r) => r.status === 'error' && r.file).forEach((r) => uploadRow(r, r.file as File));
  };

  const clearFile = (id: number) =>
    setRows((rs) =>
      rs.map((r) => {
        if (r.id !== id) return r;
        if (r.previewUrl) URL.revokeObjectURL(r.previewUrl);
        return { ...r, file: null, previewUrl: null, status: 'idle', message: undefined };
      })
    );

  const addManualRow = () => setRows((rs) => [makeRow('', true), ...rs]);

  const removeManualRow = (id: number) =>
    setRows((rs) => {
      const row = rs.find((r) => r.id === id);
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
      return rs.filter((r) => r.id !== id);
    });

  const sentCount = useMemo(() => rows.filter((r) => r.status === 'done').length, [rows]);
  const errorCount = useMemo(() => rows.filter((r) => r.status === 'error').length, [rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Manual rows always show (they're being typed); awaiting rows filter by search.
    return rows.filter((r) => r.manual || !q || r.number.toLowerCase().includes(q));
  }, [rows, search]);

  /* ── Selection ─────────────────────────────────────────── */

  const selectableVisible = useMemo(
    () => visibleRows.filter((r) => r.number.trim() && r.status !== 'done'),
    [visibleRows]
  );

  const allVisibleSelected =
    selectableVisible.length > 0 && selectableVisible.every((r) => selected.has(r.id));

  const toggleRow = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) selectableVisible.forEach((r) => next.delete(r.id));
      else selectableVisible.forEach((r) => next.add(r.id));
      return next;
    });

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.id) && r.number.trim()),
    [rows, selected]
  );

  /* ── Grouping ──────────────────────────────────────────── */

  const groupChoices: FilterOption[] = useMemo(() => {
    if (!filterOptions) return [];
    if (groupKind === 'manifest_id') return filterOptions.manifests ?? [];
    if (groupKind === 'run_manifest_id') return filterOptions.run_manifests ?? [];
    if (groupKind === 'fleet_name') return filterOptions.fleets ?? [];
    if (groupKind === 'rider') return filterOptions.riders ?? [];
    return [];
  }, [filterOptions, groupKind]);

  /** react-select options — the count rides in the label so an operator can see
   *  how big a batch is before picking it. */
  const groupSelectOptions = useMemo(
    () => groupChoices.map((c) => ({
      value: String(c.id),
      label: `${c.name || c.id} (${c.count})`,
    })),
    [groupChoices]
  );

  const selectedGroupOption = useMemo(
    () => groupSelectOptions.find((o) => o.value === String(groupValue)) ?? null,
    [groupSelectOptions, groupValue]
  );

  const groupLabel = useMemo(() => {
    if (!groupKind || !groupValue) return '';
    const match = groupChoices.find((c) => String(c.id) === String(groupValue));
    return `${GROUP_LABELS[groupKind]}: ${match?.name || groupValue}`;
  }, [groupKind, groupValue, groupChoices]);

  const onGroupKindChange = (kind: GroupKind) => {
    setGroupKind(kind);
    setGroupValue('');
    if (!kind) loadAwaiting('', '');   // cleared → back to the full backlog
  };

  const onGroupValueChange = (value: string) => {
    setGroupValue(value);
    loadAwaiting(groupKind, value);
  };

  /* ── POD sheet ─────────────────────────────────────────── */

  /** Build and download in one click — react-pdf's PDFDownloadLink would need
   *  the user to click a second time once rendering finishes. */
  const downloadPodSheet = async () => {
    if (selectedRows.length === 0) return;
    setBuildingPdf(true);
    try {
      // Resolve any hand-entered rows that haven't been looked up yet — the
      // debounce may not have fired, and an unresolved row prints blank.
      const resolved = await hydrateManualRows(selectedRows);
      const missing = resolved.filter((r) => r.notFound);
      if (missing.length) {
        toast.warn(
          `${missing.length} number(s) not found — printed without shipment details: ${missing.map((r) => r.number).join(', ')}`
        );
      }

      const blob = await pdf(
        <PodSheet
          rows={resolved}
          groupLabel={groupLabel}
          generatedAt={new Date().toLocaleString()}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      link.href = url;
      link.download = `pod_sheet_${stamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`POD sheet ready — ${selectedRows.length} shipment(s).`);
    } catch (e: any) {
      console.error('POD sheet generation failed:', e);
      toast.error('Could not generate the POD sheet.');
    } finally {
      setBuildingPdf(false);
    }
  };

  /** One photo of the signed sheet → applied to every selected shipment. The
   *  image is uploaded once server-side and shared across the batch. */
  const applyPhotoToSelected = async (file: File | null) => {
    if (!file || selectedRows.length === 0) return;

    const numbers = selectedRows.map((r) => r.number.trim()).filter(Boolean);
    setBulkApplying(true);
    selectedRows.forEach((r) => patchRow(r.id, { status: 'uploading', message: undefined }));

    const fd = new FormData();
    numbers.forEach((n) => fd.append('shipment_numbers[]', n));
    fd.append('image', file);

    try {
      const res: any = await SuperAdminApis.bulkApplyDeliveryPhoto(fd);
      const data = res?.data?.data;

      if (res?.data?.status) {
        const failures = new Map<string, string>(
          (data?.results ?? [])
            .filter((x: any) => !x.success)
            .map((x: any) => [String(x.shipment_number), x.message || 'Failed'])
        );

        selectedRows.forEach((r) => {
          const msg = failures.get(r.number.trim());
          patchRow(r.id, msg ? { status: 'error', message: msg } : { status: 'done' });
        });

        const updated = data?.updated_count ?? 0;
        setAwaitingTotal((t) => (t === null ? t : Math.max(0, t - updated)));
        setSelected(new Set());
        toast.success(
          `Photo applied to ${updated} shipment(s)${data?.failed_count ? ` · ${data.failed_count} failed` : ''}.`
        );
      } else {
        const msg = res?.data?.message || 'Bulk apply failed';
        selectedRows.forEach((r) => patchRow(r.id, { status: 'error', message: msg }));
        toast.error(msg);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Bulk apply failed';
      selectedRows.forEach((r) => patchRow(r.id, { status: 'error', message: msg }));
      toast.error(msg);
    } finally {
      setBulkApplying(false);
    }
  };

  /* ── Render helpers ────────────────────────────────────── */

  const statusBadge = (r: Row) => {
    if (r.status === 'uploading') return <Oval visible height="16" width="16" color="#E04343" secondaryColor="#eee" ariaLabel="uploading" />;
    if (r.status === 'done') return <span className="text-green-600 text-xs font-semibold">✓ Sent</span>;
    if (r.status === 'error') return <span className="text-red-600 text-[11px]" title={r.message}>✕ {r.message}</span>;
    if (r.file) return <span className="text-amber-600 text-xs font-semibold">Ready</span>;
    return <span className="text-gray-300 text-xs">—</span>;
  };

  /** Age of the POD without a photo. Colour escalates so a long backlog is
   *  obvious at a glance. */
  const hoursBadge = (r: Row) => {
    if (r.hoursAwaiting === null) return <span className="text-gray-300 text-xs">—</span>;
    const h = r.hoursAwaiting;
    const tone =
      h >= 72 ? 'bg-red-100 text-red-700'
        : h >= 24 ? 'bg-amber-100 text-amber-700'
          : 'bg-gray-100 text-gray-600';
    const label = h < 24 ? `${h}h` : `${Math.floor(h / 24)}d ${h % 24}h`;
    return (
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${tone}`} title={`${h} hours since POD`}>
        {label}
      </span>
    );
  };

  return (
    <>
      <button
        onClick={openModal}
        type="button"
        className="bg-[#E04343] rounded-[12px] p-[8px] text-white text-[12px] font-medium"
      >
        Bulk Delivery Photos
      </button>

      <Modal visible={open} width="1040" height="680" effect="fadeInUp" onClickAway={closeModal}>
        <div className="p-5 flex flex-col" style={{ height: '100%' }}>
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="font-bold">Bulk Delivery Photos</p>
              <p className="text-xs text-gray-500">
                Attach a photo per shipment, or print a POD sheet for a batch, get it signed, and apply the photo to all of them.
              </p>
            </div>
            <p className="cursor-pointer" onClick={closeModal}><SvgElement type={icontypesEnum.CANCEL} /></p>
          </div>

          {/* Grouping */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs font-semibold text-gray-600">Group by</span>
            <select
              value={groupKind}
              onChange={(e) => onGroupKindChange(e.target.value as GroupKind)}
              className="border border-gray-300 rounded-md py-1.5 px-2 text-sm"
            >
              <option value="">— None —</option>
              <option value="manifest_id">Manifest</option>
              <option value="run_manifest_id">Run Manifest</option>
              <option value="fleet_name">Fleet</option>
              <option value="rider">Rider</option>
            </select>

            <div className="min-w-[320px]">
              <Select
                options={groupSelectOptions}
                value={selectedGroupOption}
                onChange={(opt: any) => onGroupValueChange(opt?.value ?? '')}
                isDisabled={!groupKind || groupChoices.length === 0}
                isSearchable
                isClearable
                classNamePrefix="select"
                placeholder={
                  !groupKind ? 'Choose a grouping first'
                    : groupChoices.length === 0 ? 'None available'
                      : `Search ${GROUP_LABELS[groupKind].toLowerCase()}…`
                }
                noOptionsMessage={() => 'No match'}
                // The list can run to hundreds of manifests — keep the menu above
                // the row list rather than clipped inside the modal's scroll area.
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 10000 }),
                  control: (base: any) => ({ ...base, minHeight: 34, fontSize: 14 }),
                  menu: (base: any) => ({ ...base, fontSize: 13 }),
                }}
              />
            </div>

            {groupKind && groupValue && (
              <button
                onClick={() => { setGroupKind(''); setGroupValue(''); loadAwaiting('', ''); }}
                className="text-xs text-gray-500 hover:text-[#E04343] underline"
              >
                Clear
              </button>
            )}
          </div>

          {/* Search + progress */}
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search shipment number…"
                className="w-full border border-gray-300 rounded-md py-2 pl-8 pr-3 text-sm"
              />
              <span className="absolute left-2.5 top-2.5 text-gray-400 text-sm">🔍</span>
            </div>
            <div className="text-xs text-gray-600 whitespace-nowrap">
              <span className="font-semibold text-green-600">{sentCount}</span>
              {awaitingTotal !== null && <span className="text-gray-400"> of {awaitingTotal}</span>} sent
              {errorCount > 0 && <span className="text-red-500"> · {errorCount} failed</span>}
            </div>
          </div>

          {/* Batch actions */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                disabled={selectableVisible.length === 0}
                onChange={toggleAllVisible}
                className="w-4 h-4 accent-[#E04343] cursor-pointer"
              />
              Select all ({selectableVisible.length})
            </label>

            <span className="text-xs text-gray-400">|</span>

            <span className="text-xs text-gray-600">
              <span className="font-semibold text-[#E04343]">{selectedRows.length}</span> selected
            </span>

            <button
              onClick={downloadPodSheet}
              disabled={selectedRows.length === 0 || buildingPdf}
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#E04343] text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {buildingPdf ? 'Building…' : `Download POD sheet (${selectedRows.length})`}
            </button>

            <label
              className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${selectedRows.length === 0 || bulkApplying
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : 'border-[#E04343] text-[#E04343] cursor-pointer'
                }`}
            >
              {bulkApplying ? 'Applying…' : 'Apply one photo to selected'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={selectedRows.length === 0 || bulkApplying}
                onChange={(e) => {
                  applyPhotoToSelected(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
            </label>

            <span className="text-xs text-gray-400">|</span>

            <button onClick={addManualRow} className="text-[#E04343] text-xs font-semibold hover:underline">
              + Add a number manually
            </button>

            {search && (
              <span className="text-[11px] text-gray-400 ml-auto">
                {visibleRows.filter((r) => !r.manual).length} match(es)
              </span>
            )}
          </div>

          {/* Column headings */}
          <div className="flex items-center gap-3 px-1 py-1.5 border-t border-b border-gray-200 bg-gray-50 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            <span className="w-5 shrink-0" />
            <span className="w-11 shrink-0">Photo</span>
            <span className="flex-1 min-w-0">Shipment Number</span>
            <span className="w-32 shrink-0">Destination</span>
            <span className="w-16 shrink-0 text-center">Waiting</span>
            <span className="w-24 shrink-0 text-right">Status</span>
            <span className="w-[190px] shrink-0 text-right pr-1">Action</span>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto pr-1">
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <Oval visible height="28" width="28" color="#E04343" secondaryColor="#eee" ariaLabel="loading" />
              </div>
            ) : visibleRows.length === 0 ? (
              <div className="text-center text-xs text-gray-400 py-10">
                {rows.length === 0 ? 'No shipments awaiting a delivery photo. 🎉' : 'No shipment matches your search.'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {visibleRows.map((r) => {
                  const busy = r.status === 'uploading';
                  const selectable = !!r.number.trim() && r.status !== 'done';
                  return (
                    <li
                      key={r.id}
                      className={`flex items-center gap-3 py-2.5 px-1 ${r.status === 'done' ? 'bg-green-50/60'
                        : r.status === 'error' ? 'bg-red-50/40'
                          : selected.has(r.id) ? 'bg-red-50/30'
                            : ''
                        }`}
                    >
                      {/* Select */}
                      <div className="w-5 shrink-0">
                        <input
                          type="checkbox"
                          checked={selected.has(r.id)}
                          disabled={!selectable || busy}
                          onChange={() => toggleRow(r.id)}
                          className="w-4 h-4 accent-[#E04343] cursor-pointer disabled:cursor-not-allowed"
                        />
                      </div>

                      {/* Thumbnail / placeholder */}
                      <div className="w-11 h-11 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                        {r.previewUrl
                          ? <img src={r.previewUrl} alt="delivery" className="w-full h-full object-cover" />
                          : <span className="text-gray-300 text-lg">🖼</span>}
                      </div>

                      {/* Number */}
                      <div className="flex-1 min-w-0">
                        {r.manual ? (
                          <input
                            type="text"
                            value={r.number}
                            disabled={busy || r.status === 'done'}
                            onChange={(e) => patchRow(r.id, {
                              number: e.target.value,
                              status: 'idle',
                              message: undefined,
                              // Number changed — the previous lookup no longer applies.
                              hydrated: false,
                              notFound: false,
                            })}
                            placeholder="Enter shipment number"
                            className="w-full border border-gray-300 rounded-md p-1.5 text-sm disabled:bg-gray-50"
                          />
                        ) : (
                          <p className="text-sm font-medium text-gray-800 truncate">{r.number}</p>
                        )}
                        {/* Delivery date, directly under the number. Only rows that
                            came from the awaiting list (or a hydrated manual lookup)
                            have one — a freshly typed number shows nothing until it
                            has been looked up. */}
                        {r.deliveredAt && (
                          <p className="text-[11px] text-gray-500 truncate" title={`Delivered ${r.deliveredAt}`}>
                            Delivered: {fmtDelivered(r.deliveredAt)}
                          </p>
                        )}
                        {r.file && <p className="text-[11px] text-gray-400 truncate">{r.file.name}</p>}
                        {r.manual && !r.file && !r.hydrated && <p className="text-[11px] text-gray-400">Manual entry</p>}
                        {r.manual && r.notFound && (
                          <p className="text-[11px] text-amber-600">Not found — sheet will print the number only</p>
                        )}
                        {r.manual && r.hydrated && !r.notFound && r.receiverName && (
                          <p className="text-[11px] text-green-600 truncate">✓ {r.receiverName}</p>
                        )}
                      </div>

                      {/* Destination — address on the second line, full text on hover */}
                      <div className="w-32 shrink-0 min-w-0" title={[r.destination, r.address].filter(Boolean).join(' — ')}>
                        <p className="text-xs text-gray-700 truncate">
                          {r.destination || <span className="text-gray-300">—</span>}
                        </p>
                        {!!r.address && (
                          <p className="text-[10px] text-gray-400 truncate">{r.address}</p>
                        )}
                      </div>

                      {/* Hours awaiting */}
                      <div className="w-16 shrink-0 text-center">{hoursBadge(r)}</div>

                      {/* Status */}
                      <div className="w-24 text-right shrink-0">{statusBadge(r)}</div>

                      {/* Actions */}
                      <div className="w-[190px] shrink-0 flex items-center justify-end gap-2">
                        {/* Manual row with photo but not yet sent → explicit Send */}
                        {r.manual && r.file && r.number.trim() && r.status !== 'done' && !busy && (
                          <button
                            onClick={() => sendManual(r)}
                            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#E04343] text-white"
                          >
                            {r.status === 'error' ? 'Retry' : 'Send'}
                          </button>
                        )}

                        {/* Retry an awaiting-row failure with the same photo */}
                        {!r.manual && r.status === 'error' && r.file && (
                          <button
                            onClick={() => uploadRow(r, r.file as File)}
                            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#E04343] text-white"
                          >
                            Retry
                          </button>
                        )}

                        {!busy && r.status !== 'done' && (
                          <label className={`cursor-pointer text-xs font-semibold px-3 py-1.5 rounded-md border whitespace-nowrap ${r.file ? 'border-gray-200 text-gray-500' : 'border-[#E04343] text-[#E04343]'}`}>
                            {r.file ? 'Change' : 'Attach photo'}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => onPickFile(r, e.target.files?.[0] ?? null)}
                            />
                          </label>
                        )}

                        {/* Re-send a different photo for an already-sent shipment */}
                        {r.status === 'done' && (
                          <label className="cursor-pointer text-xs font-semibold px-3 py-1.5 rounded-md border border-gray-200 text-gray-400">
                            Replace
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => onPickFile(r, e.target.files?.[0] ?? null)}
                            />
                          </label>
                        )}

                        {r.file && !busy && r.status !== 'done' && (
                          <button onClick={() => clearFile(r.id)} title="Remove photo" className="text-gray-400 hover:text-red-600 text-sm">✕</button>
                        )}
                        {r.manual && !r.file && !busy && (
                          <button onClick={() => removeManualRow(r.id)} title="Remove row" className="text-gray-400 hover:text-red-600 text-sm">✕</button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="pt-3 flex items-center justify-between border-t border-gray-100 mt-2">
            <span className="text-[11px] text-gray-400">
              Photos upload automatically as you attach them. Select rows to print a POD sheet or apply one photo to the batch.
            </span>
            <div className="flex gap-2">
              {errorCount > 0 && (
                <button onClick={retryFailed} className="px-4 py-2 text-sm rounded-md border border-[#E04343] text-[#E04343] font-semibold">
                  Retry {errorCount} failed
                </button>
              )}
              <button onClick={closeModal} className="px-5 py-2 text-sm rounded-md bg-gray-100 text-gray-600 font-semibold">Done</button>
            </div>
          </div>
        </div>
      </Modal>

      <ToastContainer position="top-right" autoClose={2500} hideProgressBar />
    </>
  );
}
